import React, { useState, useEffect, useCallback } from 'react';
import { Delete, Check } from 'lucide-react';
import {
  parseExpression,
  isValidExpression,
  isWithinDigitLimit,
  endsWithOperator,
} from '../utils/expressionParser';
import { useUserPreferences } from '../contexts/UserPreferencesContext';

export interface AmountKeyboardProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (value: number) => void;
  initialValue?: number;
  allowNegative?: boolean;
  title?: string;
}

export const AmountKeyboard: React.FC<AmountKeyboardProps> = ({
  isOpen,
  onClose,
  onConfirm,
  initialValue = 0,
  allowNegative = false,
  title = 'Masukkan Jumlah',
}) => {
  const [expression, setExpression] = useState<string>('');
  const [error, setError] = useState<string>('');
  const { formatMoney, preferences } = useUserPreferences();

  // Initialize expression when keyboard opens
  useEffect(() => {
    if (isOpen) {
      // Use absolute value to handle both positive and negative initial values (e.g., debt accounts)
      setExpression(initialValue !== 0 ? Math.abs(initialValue).toString() : '');
      setError('');
    }
  }, [isOpen, initialValue]);

  // Calculate current result
  const calculatedResult = parseExpression(expression);
  const isValid = isValidExpression(expression);

  // Check if result is negative
  const isNegativeResult = calculatedResult !== null && calculatedResult < 0;

  // Handle number button press
  const handleNumberPress = (digit: string) => {
    setExpression((prev) => prev + digit);
    setError('');
  };

  // Handle operator button press - calculator style
  const handleOperatorPress = (operator: string) => {
    // Can't start with operator
    if (expression === '') {
      return;
    }

    // If expression already ends with a number (not an operator), resolve it first
    if (!endsWithOperator(expression)) {
      const result = parseExpression(expression);
      if (result !== null) {
        // Resolve current expression and start new one with the operator
        setExpression(result.toString() + operator);
        setError('');
        return;
      }
    }

    // If ends with operator, replace it with new operator
    if (endsWithOperator(expression)) {
      setExpression((prev) => prev.slice(0, -1) + operator);
      setError('');
      return;
    }

    // Otherwise just append operator
    setExpression((prev) => prev + operator);
    setError('');
  };

  // Handle backspace
  const handleBackspace = () => {
    setExpression((prev) => prev.slice(0, -1));
    setError('');
  };

  // Handle confirm
  const handleConfirm = () => {
    if (!isValid) {
      setError('Ekspresi tidak valid');
      return;
    }

    if (calculatedResult === null) {
      setError('Tidak dapat menghitung');
      return;
    }

    if (!allowNegative && isNegativeResult) {
      setError('Jumlah tidak boleh negatif');
      return;
    }

    // Vibrate if supported
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }

    onConfirm(calculatedResult);
    onClose();
  };

  // Handle keyboard events (for desktop)
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      // Numbers
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        e.stopPropagation();
        handleNumberPress(e.key);
      }
      // Operators
      else if (e.key === '+' || e.key === '-') {
        e.preventDefault();
        e.stopPropagation();
        handleOperatorPress(e.key);
      }
      // Backspace
      else if (e.key === 'Backspace') {
        e.preventDefault();
        e.stopPropagation();
        handleBackspace();
      }
      // Enter - CRITICAL: Prevent form submission
      else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        handleConfirm();
      }
      // Escape
      else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    },
    [isOpen, expression] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Attach keyboard event listener
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  // Format expression for calculator-style display with thousand separators
  const formatDisplayExpression = (expr: string): string => {
    if (!expr) return formatMoney(0);

    // Split by operators while keeping them
    const parts = expr.split(/([+\-])/);
    const formattedParts = parts.map(part => {
      // If it's an operator or empty, return as-is with spaces
      if (part === '+' || part === '-') return ` ${part} `;
      if (part === '') return '';

      // Format numbers with thousand separators
      const num = parseInt(part);
      if (!isNaN(num)) {
        return new Intl.NumberFormat('id-ID').format(num).replace(/\./g, preferences.thousandSeparator);
      }
      return part;
    });

    return preferences.currency + ' ' + formattedParts.join('').trim();
  };

  const displayExpression = formatDisplayExpression(expression);

  return (
    <div className="fixed inset-0 flex items-end md:items-center justify-center z-[100]" style={{ pointerEvents: 'none' }}>
      {/* Backdrop - transparent, passes clicks through to elements behind */}
      <div className="absolute inset-0" style={{ pointerEvents: 'none' }} />

      {/* Keyboard Container - interactive */}
      <div
        className="relative bg-white border-4 border-[#077f5d] w-full md:max-w-md md:mx-4"
        style={{ boxShadow: '8px 8px 0 #077f5d', pointerEvents: 'auto' }}
      >

        {/* Display Area */}
        <div className="bg-[#3cbe8c] text-white px-4 py-2 font-mono text-xs font-bold uppercase border-b-4 border-[#077f5d]">
          {title}
        </div>
        <div
          className="bg-[#dffbd0] border-b-4 border-[#077f5d] p-4"
        >
          {/* Calculator Display */}
          <div
            className={`font-mono text-2xl font-bold ${isNegativeResult && !allowNegative ? 'text-red-600' : 'text-[#077f5d]'
              }`}
          >
            {displayExpression}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-2 text-red-600 font-mono text-sm font-bold">{error}</div>
          )}
        </div>

        {/* Keyboard Grid */}
        <div className="p-4">
          <div className="grid grid-cols-4 gap-2">
            {/* Row 1: 7, 8, 9, Backspace */}
            <KeyboardButton type="number" onClick={() => handleNumberPress('7')}>
              7
            </KeyboardButton>
            <KeyboardButton type="number" onClick={() => handleNumberPress('8')}>
              8
            </KeyboardButton>
            <KeyboardButton type="number" onClick={() => handleNumberPress('9')}>
              9
            </KeyboardButton>
            <KeyboardButton type="backspace" onClick={handleBackspace} aria-label="Backspace">
              <Delete size={24} />
            </KeyboardButton>

            {/* Row 2: 4, 5, 6, + */}
            <KeyboardButton type="number" onClick={() => handleNumberPress('4')}>
              4
            </KeyboardButton>
            <KeyboardButton type="number" onClick={() => handleNumberPress('5')}>
              5
            </KeyboardButton>
            <KeyboardButton type="number" onClick={() => handleNumberPress('6')}>
              6
            </KeyboardButton>
            <KeyboardButton type="operator" onClick={() => handleOperatorPress('+')} aria-label="Plus">
              +
            </KeyboardButton>

            {/* Row 3: 1, 2, 3, - */}
            <KeyboardButton type="number" onClick={() => handleNumberPress('1')}>
              1
            </KeyboardButton>
            <KeyboardButton type="number" onClick={() => handleNumberPress('2')}>
              2
            </KeyboardButton>
            <KeyboardButton type="number" onClick={() => handleNumberPress('3')}>
              3
            </KeyboardButton>
            <KeyboardButton type="operator" onClick={() => handleOperatorPress('-')} aria-label="Minus">
              −
            </KeyboardButton>

            {/* Row 4: 0, 00, 000, Confirm */}
            <KeyboardButton type="number" onClick={() => handleNumberPress('0')}>
              0
            </KeyboardButton>
            <KeyboardButton type="number" onClick={() => handleNumberPress('00')}>
              00
            </KeyboardButton>
            <KeyboardButton type="number" onClick={() => handleNumberPress('000')}>
              000
            </KeyboardButton>
            <KeyboardButton
              type="confirm"
              onClick={handleConfirm}
              disabled={!isValid || (isNegativeResult && !allowNegative)}
              aria-label="Confirm"
            >
              <Check size={24} />
            </KeyboardButton>
          </div>
        </div>
      </div>
    </div>
  );
};

// Keyboard Button Component
interface KeyboardButtonProps {
  type: 'number' | 'operator' | 'backspace' | 'confirm';
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  'aria-label'?: string;
}

const KeyboardButton: React.FC<KeyboardButtonProps> = ({
  type,
  onClick,
  children,
  disabled = false,
  'aria-label': ariaLabel,
}) => {
  const [isPressed, setIsPressed] = useState(false);

  const handlePress = () => {
    if (disabled) return;

    // Vibrate on press
    if (navigator.vibrate) {
      navigator.vibrate(5);
    }

    setIsPressed(true);
    onClick();

    // Reset pressed state after a short delay
    setTimeout(() => setIsPressed(false), 100);
  };

  // Base styles
  let baseStyles = 'px-4 py-4 border-2 font-mono font-bold text-xl flex items-center justify-center min-h-[56px]';
  let bgColor = 'bg-white';
  let textColor = 'text-[#1a1a1a]';
  let borderColor = 'border-[#077f5d]';
  let shadow = '4px 4px 0 #077f5d';

  // Type-specific styles
  if (type === 'operator') {
    bgColor = 'bg-[#3cbe8c]';
    textColor = 'text-white';
  } else if (type === 'backspace') {
    bgColor = 'bg-[#f39c12]';
    textColor = 'text-white';
    borderColor = 'border-[#c87f0a]';
    shadow = '4px 4px 0 #c87f0a';
  } else if (type === 'confirm') {
    bgColor = disabled ? 'bg-gray-400' : 'bg-[#3cbe8c]';
    textColor = 'text-white';
    shadow = disabled ? '4px 4px 0 #999' : '6px 6px 0 #077f5d';
  }

  // Pressed state
  const pressedStyle = isPressed
    ? { boxShadow: '2px 2px 0 ' + shadow.split(' ')[3], transform: 'translate(2px, 2px)' }
    : { boxShadow: shadow };

  return (
    <button
      type="button"
      onClick={handlePress}
      onMouseDown={() => !disabled && setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
      onTouchStart={() => !disabled && setIsPressed(true)}
      onTouchEnd={() => setIsPressed(false)}
      disabled={disabled}
      className={`${baseStyles} ${bgColor} ${textColor} ${borderColor} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      style={{ ...pressedStyle, transition: 'none' }}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
};
